/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toValidationType } from '@data/dto/validation-type.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  ValidationType,
  ValidationTypeHttp,
  ValidationTypeWizard,
} from '@data/models/validation-type.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class ValidationTypeService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly validationTypeEndpoint = '/validation-type-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listValidationTypes(
    filters: FilterParams,
  ): Promise<PaginateResult<ValidationType[]>> {
    const { data: response, error } = await this.API?.get<{
      data: ValidationTypeHttp[];
      meta: Meta;
    }>(this.validationTypeEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toValidationType) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertValidationType({
    id,
    ...validationTypeWizard
  }: ValidationTypeWizard): Promise<ValidationType | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<ValidationTypeWizard, 'id'> },
        { data: ValidationTypeHttp }
      >(this.validationTypeEndpoint, { data: validationTypeWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toValidationType(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: ValidationTypeWizard },
      { data: ValidationTypeHttp }
    >(`${this.validationTypeEndpoint}/${id}`, {
      data: { ...validationTypeWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toValidationType(data?.data || {});
  }

  public async deleteValidationType(
    id: string,
  ): Promise<ValidationType | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: ValidationTypeHttp;
    }>(`${this.validationTypeEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toValidationType(data?.data || {});
  }

  public async sortValidationType(
    sortParams: DatatableReorderParams<ValidationType>,
  ): Promise<ValidationType | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.validationTypeEndpoint}/${id}/sort`,
      {
        data: toSortParams('validation_type_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
